/**
 * Ämnesområden för /provfragor/* – SEO-sidor som visar riktiga övningsfrågor
 * per kategori tillsammans med redaktionellt innehåll.
 * `category` måste matcha kategorinamnet i frågebanken exakt.
 */
export type ExamTopic = {
  slug: string;
  category: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  sections: { h2: string; body: string }[];
  faqs: { q: string; a: string }[];
};

export const EXAM_TOPICS: ExamTopic[] = [
  {
    slug: "tillsyn",
    category: "Tillsyn",
    h1: "Provfrågor om tillsyn – kunskapsprovet för serveringstillstånd",
    metaTitle: "Provfrågor tillsyn – kunskapsprovet serveringstillstånd",
    metaDescription:
      "Öva på provfrågor om tillsyn inför kunskapsprovet: kommunens och polisens roller, inre och yttre tillsyn, erinran, varning och återkallelse.",
    intro:
      "Tillsyn är det största enskilda ämnesområdet i vår frågebank och ett återkommande tema på kommunens kunskapsprov. Här övar du på frågor om vem som utövar tillsyn, vilka former tillsynen tar och vilka sanktioner som kan följa på brister.",
    sections: [
      {
        h2: "Vem utövar tillsyn enligt alkohollagen?",
        body: "Tillsynsansvaret är delat. Kommunen och Polismyndigheten har den omedelbara tillsynen över serveringsställen i kommunen. Länsstyrelsen utövar tillsyn inom länet och ska biträda kommunerna med råd. Folkhälsomyndigheten ansvarar för den nationella tillsynsvägledningen tillsammans med Länsstyrelserna. På provet är det vanligt att man ska kunna skilja dessa roller åt – särskilt att kommunen fattar besluten om tillstånd och sanktioner, medan Folkhälsomyndigheten inte gör det.",
      },
      {
        h2: "Inre, yttre och förebyggande tillsyn",
        body: "Förebyggande tillsyn är information, utbildning och rådgivning till tillståndshavare. Inre tillsyn sker vid skrivbordet och granskar bokföring, skatteinbetalningar, ägarförhållanden och restaurangrapporter, ofta i samverkan med Skatteverket och Polismyndigheten. Yttre tillsyn är besöket på serveringsstället, som normalt sker oanmält under pågående servering. Provfrågor testar ofta om du kan placera en beskriven situation i rätt kategori.",
      },
      {
        h2: "Sanktionstrappan: erinran, varning och återkallelse",
        body: "Vid mindre allvarliga brister kan kommunen meddela en erinran. Vid allvarligare eller upprepade brister ges en varning. Är bristerna så allvarliga att en varning inte är tillräcklig – eller upprepas de trots varning – kan tillståndet återkallas enligt 9 kap. 18 §. Återkallelse sker också om tillståndet inte längre utnyttjas eller om tillståndshavaren inte längre uppfyller kraven på lämplighet.",
      },
      {
        h2: "Vad kommunen tittar på vid ett besök",
        body: "Vanliga kontrollpunkter är: vem som är serveringsansvarig på plats, om egenkontrollprogrammet finns tillgängligt, hur ålderskontrollen fungerar i entrén, om det förekommer överservering, om serveringstiderna hålls och om marknadsföringen är måttfull. Att kunna visa upp dokumenterade rutiner och ifyllda loggar är den enklaste vägen till ett besök utan anmärkning.",
      },
      {
        h2: "Tillsynsavgifter och restaurangrapport",
        body: "Kommunen får ta ut avgift för både prövning och tillsyn. Den årliga tillsynsavgiften bygger normalt på en fast del och en rörlig del kopplad till alkoholomsättningen, som redovisas i restaurangrapporten. Rapporten lämnas i regel en gång per år och är samtidigt en del av kommunens inre tillsyn.",
      },
    ],
    faqs: [
      { q: "Vem beslutar om varning och återkallelse?", a: "Kommunen fattar beslutet. Polismyndigheten är tillsynsmyndighet men beslutar inte om sanktioner enligt alkohollagen." },
      { q: "Måste tillsynsbesök aviseras i förväg?", a: "Nej. Yttre tillsyn sker normalt oanmält, ofta under kvällar och helger när verksamheten är i full drift." },
      { q: "Kan tillståndet återkallas direkt utan varning?", a: "Ja, om bristerna är så allvarliga att en varning inte är tillräcklig, till exempel vid brottslig verksamhet eller allvarlig ekonomisk misskötsamhet." },
      { q: "Vad är skillnaden mellan erinran och varning?", a: "Erinran är den lindrigaste åtgärden vid mindre brister. Varning används vid allvarligare eller upprepade brister och är ett steg närmare återkallelse." },
      { q: "Hur många frågor om tillsyn finns i frågebanken?", a: "Tillsyn är vårt största ämnesområde med närmare hundra aktiva frågor. Ett urval fria frågor visas här; hela banken ingår i Premium." },
    ],
  },
  {
    slug: "alkoholpolitik",
    category: "Alkoholpolitik",
    h1: "Provfrågor om alkoholpolitik – kunskapsprovet",
    metaTitle: "Provfrågor alkoholpolitik – kunskapsprovet 2026",
    metaDescription:
      "Öva på provfrågor om svensk alkoholpolitik: folkhälsomålen, detaljhandelsmonopolet, tillgänglighetsbegränsning och alkohollagens syfte.",
    intro:
      "Alkoholpolitik handlar om varför reglerna ser ut som de gör. Kunskapsprovet innehåller frågor om alkohollagens syfte, monopolordningen och hur begränsad tillgänglighet används som folkhälsoverktyg.",
    sections: [
      {
        h2: "Alkohollagens syfte",
        body: "Alkohollagen är en skyddslagstiftning. Syftet är att begränsa alkoholens skadeverkningar, och när det uppstår konflikt mellan näringspolitiska och alkoholpolitiska hänsyn ska de alkoholpolitiska väga tyngst. Det är en av de mest testade principerna på provet: skyddet för folkhälsan går före enskilda verksamheters intressen.",
      },
      {
        h2: "Detaljhandelsmonopolet",
        body: "Systembolaget har ensamrätt till detaljhandel med spritdrycker, vin, starköl och andra jästa alkoholdrycker. Servering är däremot tillståndspliktig verksamhet som prövas av kommunen. Folköl (högst 3,5 volymprocent) får säljas i vanlig detaljhandel under vissa förutsättningar, bland annat att butiken också säljer mat och har anmält försäljningen till kommunen.",
      },
      {
        h2: "Tillgänglighet som styrmedel",
        body: "Svensk alkoholpolitik bygger på att minskad tillgänglighet minskar den totala konsumtionen och därmed skadorna. Åldersgränser, begränsade serveringstider, monopol, prisinstrument och reklambegränsningar är alla uttryck för samma tanke. Provfrågor tar ofta upp hur dessa verktyg hänger ihop.",
      },
      {
        h2: "Måttfullhet i marknadsföring",
        body: "Kravet på särskild måttfullhet i marknadsföringen är alkoholpolitiskt motiverat: reklam ska inte vara påträngande, uppsökande eller uppmana till bruk. Det gäller lika mycket för inlägg i sociala medier som för traditionella annonser.",
      },
      {
        h2: "Kommunens roll i alkoholpolitiken",
        body: "Varje kommun antar egna riktlinjer för serveringstillstånd. Riktlinjerna får inte gå utöver lagen men beskriver hur kommunen tillämpar den, till exempel gällande serveringstider, uteserveringar och närhet till skolor eller idrottsanläggningar.",
      },
    ],
    faqs: [
      { q: "Vad väger tyngst – näringspolitik eller alkoholpolitik?", a: "Alkoholpolitiska hänsyn väger tyngre. Det framgår uttryckligen av förarbetena till alkohollagen och är en vanlig provfråga." },
      { q: "Får kommunen ha egna alkoholpolitiska riktlinjer?", a: "Ja, kommunen ska ha riktlinjer för sin tillämpning, men de får inte ställa krav utöver vad alkohollagen medger." },
      { q: "Varför finns detaljhandelsmonopolet?", a: "Monopolet motiveras av folkhälsoskäl: att hålla vinstintresset borta från detaljhandeln minskar konsumtionsdrivande försäljning." },
      { q: "Vad räknas som folköl?", a: "Öl med en alkoholhalt över 2,25 men högst 3,5 volymprocent. Starköl har högre halt och får bara säljas av Systembolaget eller serveras med tillstånd." },
      { q: "Behöver jag kunna alkoholpolitik på provet?", a: "Ja. Provet innehåller frågor om lagens syfte och de alkoholpolitiska grunderna, inte bara om praktiska driftsregler." },
    ],
  },
  {
    slug: "serveringsansvar",
    category: "Serveringsansvar",
    h1: "Provfrågor om serveringsansvar – kunskapsprovet",
    metaTitle: "Provfrågor serveringsansvar – kunskapsprovet",
    metaDescription:
      "Öva på provfrågor om serveringsansvar: serveringsansvarig person, tillståndshavarens ansvar, närvarokrav och ansvar vid överservering.",
    intro:
      "Serveringsansvar handlar om vem som bär ansvaret när något går fel i serveringen. Provet testar både tillståndshavarens övergripande ansvar och den serveringsansvariges roll under passet.",
    sections: [
      {
        h2: "Tillståndshavarens ansvar",
        body: "Tillståndshavaren ansvarar för att verksamheten drivs enligt alkohollagen och för att personalen kan reglerna. Ansvaret kan inte avtalas bort. Det är också tillståndshavaren som drabbas av sanktioner om brister uppstår, även när felet begicks av en anställd.",
      },
      {
        h2: "Serveringsansvarig person",
        body: "Under hela serveringstiden ska tillståndshavaren eller en av denne utsedd serveringsansvarig person finnas på serveringsstället. Serveringsansvarig ska ha fyllt 20 år och vara lämplig för uppgiften, och personerna ska anmälas till kommunen. Att kunna dessa krav utantill är en klassisk provfråga.",
      },
      {
        h2: "Vem får servera?",
        body: "Den som serverar alkoholdrycker ska ha fyllt 20 år. Undantag gäller för servering av folköl och för lärlingar under utbildning i vissa fall. Personal under 20 år kan alltså arbeta i lokalen men inte sköta alkoholserveringen självständigt.",
      },
      {
        h2: "Ansvar vid överservering",
        body: "Servering ska nekas till den som är märkbart påverkad. Ansvaret ligger hos den som faktiskt serverar, men tillståndshavaren ansvarar för att rutinerna finns och följs. Dokumenterade rutiner och genomförd personalutbildning är det som gör ansvaret hanterbart i praktiken.",
      },
      {
        h2: "Ansvar vid personalfest och slutna sällskap",
        body: "Även vid slutna sällskap och personalarrangemang gäller alkohollagens regler om ålder, nykterhet och ordning. Serveringsansvarig ska vara på plats och tillståndets omfattning avgör vad som är tillåtet.",
      },
    ],
    faqs: [
      { q: "Hur gammal måste en serveringsansvarig vara?", a: "Minst 20 år. Personen ska dessutom vara lämplig för uppgiften och anmäld till kommunen." },
      { q: "Måste serveringsansvarig vara på plats hela tiden?", a: "Ja, tillståndshavaren eller en utsedd serveringsansvarig ska finnas på serveringsstället under hela serveringstiden." },
      { q: "Vem straffas om en anställd serverar en minderårig?", a: "Den anställde kan ha ett straffrättsligt ansvar, men tillståndshavaren bär det tillståndsrättsliga ansvaret och riskerar erinran, varning eller återkallelse." },
      { q: "Får en 18-åring servera starköl?", a: "Nej. Den som serverar alkoholdrycker ska ha fyllt 20 år, med begränsade undantag." },
      { q: "Måste serveringsansvariga anmälas skriftligt?", a: "Ja, kommunen ska ha en aktuell förteckning. Rutinen bör ingå i ert egenkontrollprogram." },
    ],
  },
  {
    slug: "alderskontroll",
    category: "Ålderskontroll",
    h1: "Provfrågor om ålderskontroll – kunskapsprovet",
    metaTitle: "Provfrågor ålderskontroll – kunskapsprovet",
    metaDescription:
      "Öva på provfrågor om ålderskontroll: 18-årsgränsen, godkända id-handlingar, langning och hur du hanterar tveksamma fall vid servering.",
    intro:
      "Ålderskontroll är den rutin som testas hårdast både på provet och i verkligheten. Här övar du på frågor om åldersgränser, legitimation och langning.",
    sections: [
      {
        h2: "Åldersgränser i alkohollagen",
        body: "Vid servering är åldersgränsen 18 år. För detaljhandel på Systembolaget gäller 20 år. Folköl får säljas till den som fyllt 18 år. Den som serverar alkoholdrycker ska ha fyllt 20 år. Provfrågor blandar ofta dessa gränser för att testa om du kan hålla isär dem.",
      },
      {
        h2: "Godkända identitetshandlingar",
        body: "Svenskt körkort, svenskt nationellt id-kort, SIS-märkt id-kort, Skatteverkets id-kort och pass används i praktiken. Kopior, foton i mobilen och handlingar utan foto godtas inte. Personalen ska kontrollera foto, födelsedatum och giltighetstid.",
      },
      {
        h2: "25-årsregeln",
        body: "Att begära legitimation av alla som ser ut att vara under 25 år är inte ett lagkrav utan en beprövad branschrutin. Den ger marginal och gör kontrollen konsekvent, vilket är precis vad kommunen vill se i egenkontrollprogrammet.",
      },
      {
        h2: "Langning och vidarelämning",
        body: "Det är förbjudet att lämna vidare alkohol till den som är under 18 år eller till någon som är märkbart påverkad. Personalen ska ha uppsikt över borden och ingripa när drycker vandrar. Rutinen bör beskriva hur ni agerar och hur händelsen noteras.",
      },
      {
        h2: "Vid tveksamhet",
        body: "Ingen legitimation – ingen alkohol. Beslutet ska inte förhandlas med gästen. En tydlig och enhetlig rutin skyddar både personalen och tillståndet.",
      },
    ],
    faqs: [
      { q: "Vilken åldersgräns gäller vid servering?", a: "18 år vid servering av alkoholdrycker på serveringsställe med tillstånd." },
      { q: "Måste jag alltid begära legitimation?", a: "Lagen kräver kontroll när det finns anledning att anta att gästen är under 18. Praktisk branschrutin är att alltid begära id av alla som ser ut att vara under 25." },
      { q: "Är 25-årsregeln lag?", a: "Nej, det är en frivillig rutin som ger marginal. Den bör dokumenteras i egenkontrollprogrammet." },
      { q: "Räcker en bild av körkortet i mobilen?", a: "Nej. Endast fysiska, giltiga id-handlingar med foto godtas." },
      { q: "Vad händer vid överträdelse?", a: "Servering till underårig kan leda till varning eller återkallat tillstånd och kan även vara straffbart." },
    ],
  },
  {
    slug: "marknadsforing",
    category: "Marknadsföring",
    h1: "Provfrågor om marknadsföring av alkohol – kunskapsprovet",
    metaTitle: "Provfrågor marknadsföring alkohol – kunskapsprovet",
    metaDescription:
      "Öva på provfrågor om alkoholmarknadsföring: särskild måttfullhet, prissättning, happy hour, sociala medier och förbjudna erbjudanden.",
    intro:
      "Marknadsföringsreglerna är konkreta och lätta att testa på ett prov – och lätta att bryta mot i verkligheten. Här övar du på frågor om måttfullhet, prissättning och kampanjer.",
    sections: [
      {
        h2: "Kravet på särskild måttfullhet",
        body: "All marknadsföring av alkoholdrycker ska vara särskilt måttfull. Den får inte vara påträngande, uppsökande eller uppmana till bruk av alkohol. Bilder och texter ska inte framställa alkohol som lösning på problem eller koppla den till framgång, idrott eller bilkörning.",
      },
      {
        h2: "Prissättning och mängdrabatter",
        body: "Priset för en större serveringsvolym får inte vara lägre per volymenhet än för en mindre. Erbjudanden som premierar större mängd – två för en, fri bar mot inträde, rabatt vid flera glas – strider mot måttfullhetskravet.",
      },
      {
        h2: "Happy hour",
        body: "Tidsbegränsade prissänkningar är inte förbjudna i sig, men de får inte utformas så att de uppmanar till hög konsumtion, och de ska omfattas av samma måttfullhetskrav i marknadsföringen. Kommunernas riktlinjer kan ge ytterligare vägledning.",
      },
      {
        h2: "Sociala medier",
        body: "Inlägg på egna kanaler är marknadsföring. Samma regler gäller där som i tryckt annons. Ha en rutin för vem som godkänner inlägg och undvik att dela gästers bilder som visar hög konsumtion.",
      },
      {
        h2: "Alkoholfria alternativ",
        body: "Alkoholfria alternativ ska finnas och synas i menyn. Att lyfta fram dem är både ett lagkrav i praktiken och något kommunen noterar vid tillsyn.",
      },
    ],
    faqs: [
      { q: "Är happy hour tillåtet?", a: "Tidsbegränsade priser är inte förbjudna i sig, men marknadsföringen måste vara särskilt måttfull och får inte uppmana till hög konsumtion." },
      { q: "Får jag sälja två drinkar till priset av en?", a: "Nej. Mängdrabatter som gör större volym billigare per enhet är inte förenliga med måttfullhetskravet." },
      { q: "Gäller reglerna för Instagram och TikTok?", a: "Ja. Inlägg i egna sociala kanaler är marknadsföring och omfattas av samma krav." },
      { q: "Måste menyn innehålla alkoholfria alternativ?", a: "Ja, alkoholfria alternativ ska finnas tillgängliga och synas i utbudet." },
      { q: "Får jag skriva ut alkoholhalt i menyn?", a: "Ja, saklig produktinformation är tillåten. Det är framställningen som ska vara måttfull." },
    ],
  },
  {
    slug: "egenkontroll",
    category: "Egenkontroll",
    h1: "Provfrågor om egenkontroll – kunskapsprovet",
    metaTitle: "Provfrågor egenkontroll – kunskapsprovet 2026",
    metaDescription:
      "Öva på provfrågor om egenkontroll: egenkontrollprogrammets innehåll, rutiner, loggar, dokumentation och vad kommunen kräver vid tillsyn.",
    intro:
      "Egenkontroll är kopplingen mellan lagtext och vardag. Provet testar vad ett egenkontrollprogram ska innehålla och varför dokumentationen spelar roll.",
    sections: [
      {
        h2: "Vad är ett egenkontrollprogram?",
        body: "Ett skriftligt dokument som beskriver hur ni säkerställer att alkohollagen följs i den dagliga driften. Det ska finnas tillgängligt på serveringsstället och kunna visas vid tillsyn. Kommunen begär det normalt redan i samband med ansökan.",
      },
      {
        h2: "Obligatoriska delar i praktiken",
        body: "Ansvarsfördelning, rutin för ålderskontroll, rutin mot överservering och langning, ordning och säkerhet, marknadsföring, inköp och bokföring samt personalintroduktion. Till detta kommer loggar och checklistor som visar att rutinerna används. Matutbud är sedan 1 juni 2026 inte längre ett lagkrav och behöver bara beskrivas om ni serverar mat.",
      },
      {
        h2: "Dokumentation som bevis",
        body: "Ifylld incidentlogg och logg för nekad servering är starka bevis vid tillsyn. En tom logg tolkas sällan som att inga problem funnits, utan som att ingen kontroll gjorts.",
      },
      {
        h2: "När ska programmet uppdateras?",
        body: "Vid varje väsentlig förändring – ny serveringsansvarig, ändrade öppettider, ny ägarstruktur, ombyggnation – och därutöver minst en gång per år. Datera varje revidering.",
      },
      {
        h2: "Egenkontroll enligt alkohollagen vs livsmedelslagen",
        body: "Två olika regelverk. Livsmedelsegenkontrollen (HACCP) rör köket, alkoholegenkontrollen rör serveringen. De flesta restauranger behöver båda och bör hålla dem åtskilda men lätta att hitta.",
      },
    ],
    faqs: [
      { q: "Är egenkontrollprogram obligatoriskt?", a: "Kommunen kräver i praktiken ett skriftligt egenkontrollprogram vid ansökan, och det ska finnas tillgängligt på serveringsstället vid tillsyn." },
      { q: "Var ska programmet förvaras?", a: "På serveringsstället, åtkomligt för personalen under passet – inte bara digitalt hos ägaren." },
      { q: "Hur ofta ska det uppdateras?", a: "Vid varje väsentlig förändring och minst en gång per år, med daterad revidering." },
      { q: "Räcker det med en nedladdad mall?", a: "Nej, mallen måste anpassas till er verksamhet. En oanpassad mall är en vanlig anmärkningsorsak." },
      { q: "Vad är skillnaden mot livsmedelsegenkontroll?", a: "Livsmedelsegenkontrollen gäller kökets hantering enligt livsmedelslagstiftningen; alkoholegenkontrollen gäller serveringen enligt alkohollagen." },
    ],
  },
];

export function getExamTopic(slug: string): ExamTopic | undefined {
  return EXAM_TOPICS.find((t) => t.slug === slug);
}
